/**
 * Who is behind this.
 *
 * Short, and honest about being small. Two people building a poker product is not a
 * weakness to hide — it is the reason the claims on the rest of the site can be as
 * specific as they are, because there is nobody in the middle turning "solved for
 * short stacks" into "solved".
 *
 * The bracketed values are the founders' own words about themselves and only they
 * can write them. As on the legal pages, anything unfilled shows a banner rather
 * than shipping quietly as a placeholder.
 */
import { Link } from "react-router-dom";
import { LandingSections } from "./Landing";
import { PurityIngot } from "../lib/purity";

const FOUNDERS = [
  {
    name: "Tim Paquette",
    role: "[ROLE]",
    bio: "[TIM'S BIO — a couple of sentences: how long you have played, what you play, and why this exists]",
  },
  {
    name: "[COFOUNDER NAME]",
    role: "[ROLE]",
    bio: "[COFOUNDER'S BIO]",
  },
] as const;

const unfilled = FOUNDERS.flatMap((f) =>
  [f.name, f.role, f.bio].filter((v) => v.includes("["))).length;

export function About() {
  return (
    <div className="page prose">
      {unfilled > 0 && (
        <div className="legal-todo" role="alert">
          <strong>Not finished.</strong> {unfilled} value{unfilled === 1 ? "" : "s"} in{" "}
          <code>web/src/routes/About.tsx</code> still need writing. Everyone who opens
          this page sees this banner until they are filled in.
        </div>
      )}

      <h1>About</h1>
      <p className="lede">
        BluffBusters is two people. We play tournaments, we got tired of paying for
        training tools that would not say where their numbers came from, and we built
        the one we wanted.
      </p>

      <h2>Why it looks like this</h2>
      <p>
        Most poker training software has the same problem: it labels everything
        "GTO" and never says which parts were solved, which were approximated, and
        which were guessed. Once a product does that, you cannot tell its good work
        from its filler — and neither can the person who made it.
      </p>
      <p>
        So we drew a line down the middle of our own product. The preflop charts come
        out of a solver we wrote, and each one carries the measured gap between that
        solver's model and the real game — printed on the chart, in the same units we
        gate on. Where the gap is bigger than what that kind of chart is allowed, we
        withhold it rather than shipping an approximation of it. The bots are a
        heuristic and are described as a heuristic everywhere they appear, including in
        the places where calling them something grander would sell better. The{" "}
        <Link to="/fair">shuffle</Link> can be checked by anyone who wants to check it,
        and that page lists what we are <em>not</em> claiming as prominently as what we
        are.
      </p>
      <p>
        None of that is generosity. It is the only way the claims we do make are worth
        anything.
      </p>

      <h2>How we mark a chart</h2>
      <p>
        Every solved chart is measured against the game it is meant to describe, and
        stamped with what that measurement found. The stamp works like an assay: the
        metal is the standard it met, and the figure struck into it is the actual
        purity. You never get one without the other.
      </p>

      <div className="assay">
        <div>
          <PurityIngot tier="gold" />
          <h3>Gold</h3>
          <p>
            Wrong by less than <span className="n">0.24%</span> of the pot. That number
            is not ours — it is the average EV loss GTO Wizard publishes for its
            three-way solutions, and it is the only published standard we know of for
            the hard case. We hold our best tier to somebody else&rsquo;s bar on purpose.
          </p>
        </div>
        <div>
          <PurityIngot tier="silver" />
          <h3>Silver</h3>
          <p>
            Wrong by less than <span className="n">0.50%</span> of the pot. Almost all of
            these are short-handed, short-stack spots &mdash; three players at six big
            blinds, and the like. They are harder to model exactly because several
            players go all in at once, which is the situation our solver simplifies. The
            chart is not lesser work; the spot is harder.
          </p>
        </div>
      </div>

      <p>
        Past half a percent we do not publish at all. Thirty-two combinations are marked
        withheld rather than sold to you with a caveat, and they stay withheld until the
        model that produces them is better &mdash; not until the wording is.
      </p>

      <h2>What we will claim, and what we will not</h2>
      <p>
        Two numbers get confused everywhere in this industry, and we separate them
        because they fail differently. <b>Convergence</b> is how close a solve came to
        equilibrium inside its own model; ours run near{" "}
        <span className="n">0.004%</span>, and the worst in the library is{" "}
        <span className="n">0.04%</span>. <b>Model error</b> is how far that model sits
        from real poker. It is the larger number, it is the one that can actually cost
        you money, and it is the one we stamp on the chart. A product that quotes only
        the first is quoting the flattering one.
      </p>
      <p>
        Where we can be measured against somebody else, we are. Our gold bar is set to a
        competitor&rsquo;s published figure rather than a number we picked, and our
        accounting is deliberately stricter than the comparison needs: we take the{" "}
        <b>worst node</b> in a chart rather than an average across spots, we add{" "}
        <b>two standard errors</b> before judging, and we count convergence error{" "}
        <b>on top of</b> model error instead of instead of it.
      </p>
      <p>
        The honest caveat, which belongs here and not only in our source code: their
        error is an approximate solve of the right game, ours is an exact solve of a
        simplified one. Ours is systematic &mdash; it leans the same way every time
        &mdash; where theirs is closer to noise. At these magnitudes the practical
        difference is small, but it is a difference, and you should know it exists
        before you take our bar as better than theirs.
      </p>
      <p>
        We would rather lose a comparison honestly than win one by picking the number
        that suits us. Everything above is checkable: the measurement code, the sample
        sizes and the per-chart figures all ship in the open, and the trainer prints each
        chart&rsquo;s own error next to the hand you are looking at.
      </p>
      <p className="muted small">
        More detail on the method &mdash; how the measurement is run, how many hands it
        deals, and what it does with the result &mdash; is being written up and will
        appear here shortly.
      </p>

      <h2>Who</h2>
      <div className="two-up">
        {FOUNDERS.map((f) => (
          <div key={f.name}>
            <h3>{f.name}</h3>
            <p className="muted small">{f.role}</p>
            <p>{f.bio}</p>
          </div>
        ))}
      </div>

      <h2>Where it is going</h2>
      <p>
        The solved library covers short stacks — ten to twenty-five big blinds,
        push/fold and ICM, heads-up through nine-handed. Deeper stacks and postflop
        solves are the obvious next thing and they are genuinely hard; we would rather
        ship them late than ship an approximation with a confident label on it.
      </p>

      <h2>Talk to us</h2>
      <p>
        <a href="mailto:support@bluffbusters.net">support@bluffbusters.net</a>. Bug
        reports especially — if you find a hand where the deck does not verify, or a
        chart that disagrees with a solve you trust, we want it more than we want the
        compliment.
      </p>

      {/*
        * PHASE 2B: the landing page moved here, intact.
        *
        * Home became the saloon — three doors, a name, a balance and a chalkboard — and
        * these four sections are everything the old landing page said: what the trainer
        * is, what the table is, what we will not tell you, that the deck is checkable,
        * and what it costs. Not one word was rewritten, because every one of them is a
        * claim about accuracy and a paraphrase of a claim is a new claim.
        */}
      <LandingSections />
    </div>
  );
}
